/**
 * HTTP service for VAU instance CRUD + sub-resources.
 *
 * Mirrors the pattern in `features/agents/services/specialized-agent.ts`:
 * each function takes its inputs and returns an async fetcher so the
 * call site can hand it to TanStack Query directly. `.key()` on each
 * function produces the matching query-key tuple — keeps invalidation
 * targeted (no string duplication between fetchers and `invalidateQueries`).
 */

import http from '@frontend/api/http';
import { HYPATIA_URL } from '@frontend/envConfig';

import type {
  FilterRulesUpdatePayload,
  HiredDirectoryTemplatesResponse,
  PersonaUpdatePayload,
  PrincipalGrantPayload,
  ProductConnectionsUpdatePayload,
  PublishVAUPayload,
  PublishVAUResponse,
  TeamMemberAddPayload,
  VAUExport,
  VAUImportPayload,
  VAUImportResponse,
  VAUInstanceCreatePayload,
  VAUInstanceListParams,
  VAUInstanceListResponse,
  VAUInstanceResponse,
  VAUInstanceUpdatePayload,
  VAUStatus,
} from '../types';

const base = (instanceId: string) => `${HYPATIA_URL}/api/v1/${instanceId}/vau/instances`;

/**
 * Per-call axios timeout for any VAU API that mints (or refreshes) a
 * fresh OBO token. Token-service's `/vau-tokens/{vau_id}` does an
 * identity-api → Okta round-trip on cache miss — measured at ~14s
 * (vs ~85ms once cached). The global axios timeout is 10s, correct
 * for every other API call but it aborts these calls before the
 * backend completes the cold mint, surfacing a bogus generic error
 * toast while the backend has already succeeded.
 *
 * Apply this to ANY new endpoint that provisions an identity, assumes
 * a VAU, or otherwise triggers `provision_virtual_user` /
 * `perform_vau_login` on the backend. Current users:
 *   - createVAU
 *   - importVAU
 *   - retryProvisioningVAU
 *   - assumeVAU (in ./vau-context.ts)
 */
export const VAU_PROVISIONING_TIMEOUT_MS = 60_000;

// --- List -----------------------------------------------------------

export function listVAUs(instanceId: string, params: VAUInstanceListParams = {}) {
  return async () => {
    const res = await http.get<VAUInstanceListResponse>(base(instanceId), { params });
    return res.data;
  };
}
listVAUs.key = (instanceId: string, params: VAUInstanceListParams = {}) =>
  ['vau', 'list', instanceId, params] as const;

// --- Get one --------------------------------------------------------

export function getVAU(instanceId: string, vauId: string) {
  return async () => {
    const res = await http.get<VAUInstanceResponse>(
      `${base(instanceId)}/${encodeURIComponent(vauId)}`
    );
    return res.data;
  };
}
getVAU.key = (instanceId: string, vauId: string) => ['vau', 'detail', instanceId, vauId] as const;

// --- Get by Opti-ID user id -----------------------------------------

export function getVAUByOptiId(instanceId: string, optiidUserId: string) {
  return async () => {
    const res = await http.get<VAUInstanceResponse>(
      `${base(instanceId)}/by-optiid/${encodeURIComponent(optiidUserId)}`
    );
    return res.data;
  };
}
getVAUByOptiId.key = (instanceId: string, optiidUserId: string) =>
  ['vau', 'by-optiid', instanceId, optiidUserId] as const;

// --- Mutations: create / update / pause / resume / delete -----------

export async function createVAU(instanceId: string, payload: VAUInstanceCreatePayload) {
  // Provisions a fresh OBO token; see VAU_PROVISIONING_TIMEOUT_MS rationale.
  const res = await http.post<VAUInstanceResponse>(base(instanceId), payload, {
    timeout: VAU_PROVISIONING_TIMEOUT_MS,
  });
  return res.data;
}

export async function updateVAU(
  instanceId: string,
  vauId: string,
  payload: VAUInstanceUpdatePayload
) {
  const res = await http.put<VAUInstanceResponse>(
    `${base(instanceId)}/${encodeURIComponent(vauId)}`,
    payload
  );
  return res.data;
}

export async function pauseVAU(instanceId: string, vauId: string) {
  const res = await http.post<VAUInstanceResponse>(
    `${base(instanceId)}/${encodeURIComponent(vauId)}/pause`
  );
  return res.data;
}

export async function resumeVAU(instanceId: string, vauId: string) {
  const res = await http.post<VAUInstanceResponse>(
    `${base(instanceId)}/${encodeURIComponent(vauId)}/resume`
  );
  return res.data;
}

export async function deprovisionVAU(instanceId: string, vauId: string) {
  await http.delete(`${base(instanceId)}/${encodeURIComponent(vauId)}`);
}

/**
 * AC4/H4: re-run identity provisioning for a VAU stuck in
 * `provisioning_failed`. Backend's `_grant_idempotent` skips grants
 * that already succeeded so this is safe to call any number of times.
 * On success the VAU flips back to `active`; the response carries the
 * per-grant detail so the UI can show what was retried.
 */
export type RetryProvisioningResponse = {
  status: VAUStatus;
  optiid_user_id: string;
  email: string;
  opal_grant: 'ok' | 'failed';
  product_grants: Array<{
    instance_id: string;
    product_id: string;
    status: 'ok' | 'skipped' | 'failed';
    error: string | null;
  }>;
};

export async function retryProvisioningVAU(instanceId: string, vauId: string) {
  // Re-runs grants and may hit the same cold OBO mint; see VAU_PROVISIONING_TIMEOUT_MS.
  const res = await http.post<RetryProvisioningResponse>(
    `${base(instanceId)}/${encodeURIComponent(vauId)}/retry-provisioning`,
    undefined,
    { timeout: VAU_PROVISIONING_TIMEOUT_MS }
  );
  return res.data;
}

// --- Principals -----------------------------------------------------

export async function grantPrincipal(
  instanceId: string,
  vauId: string,
  payload: PrincipalGrantPayload
) {
  const res = await http.post<VAUInstanceResponse>(
    `${base(instanceId)}/${encodeURIComponent(vauId)}/principals`,
    payload
  );
  return res.data;
}

export async function revokePrincipal(instanceId: string, vauId: string, userId: string) {
  const res = await http.delete<VAUInstanceResponse>(
    `${base(instanceId)}/${encodeURIComponent(vauId)}/principals/${encodeURIComponent(userId)}`
  );
  return res.data;
}

/**
 * Re-push the current principal set to Turnstile for a VAU whose last
 * update-time push failed (status `principals_out_of_sync`). The push is
 * idempotent; on success the row heals back to `active`, otherwise it stays
 * `principals_out_of_sync` and `synced` is false. Mirrors `retryProvisioningVAU`
 * but repairs the Admin Center display sync rather than identity grants.
 */
export type SyncPrincipalsResponse = {
  status: VAUStatus;
  synced: boolean;
};

export async function syncPrincipals(instanceId: string, vauId: string) {
  const res = await http.post<SyncPrincipalsResponse>(
    `${base(instanceId)}/${encodeURIComponent(vauId)}/sync-principals`
  );
  return res.data;
}

// --- Team members ---------------------------------------------------

export async function addTeamMember(
  instanceId: string,
  vauId: string,
  payload: TeamMemberAddPayload
) {
  const res = await http.post<VAUInstanceResponse>(
    `${base(instanceId)}/${encodeURIComponent(vauId)}/team-members`,
    payload
  );
  return res.data;
}

export async function removeTeamMember(instanceId: string, vauId: string, userId: string) {
  const res = await http.delete<VAUInstanceResponse>(
    `${base(instanceId)}/${encodeURIComponent(vauId)}/team-members/${encodeURIComponent(userId)}`
  );
  return res.data;
}

// --- Filter rules ---------------------------------------------------

export async function updateFilterRules(
  instanceId: string,
  vauId: string,
  payload: FilterRulesUpdatePayload
) {
  const res = await http.put<VAUInstanceResponse>(
    `${base(instanceId)}/${encodeURIComponent(vauId)}/filters`,
    payload
  );
  return res.data;
}

// --- Product connections --------------------------------------------

/**
 * Set which product instance the teammate operates in, per product.
 * Full replace — see `ProductConnectionsUpdatePayload`.
 */
export async function updateProductConnections(
  instanceId: string,
  vauId: string,
  payload: ProductConnectionsUpdatePayload
) {
  const res = await http.put<VAUInstanceResponse>(
    `${base(instanceId)}/${encodeURIComponent(vauId)}/product-connections`,
    payload
  );
  return res.data;
}

// --- Persona --------------------------------------------------------

export async function updatePersona(
  instanceId: string,
  vauId: string,
  payload: PersonaUpdatePayload
) {
  const res = await http.put<VAUInstanceResponse>(
    `${base(instanceId)}/${encodeURIComponent(vauId)}/persona`,
    payload
  );
  return res.data;
}

// --- Export / Import ------------------------------------------------

/**
 * Export a VAU as a portable JSON envelope. Carries the self-contained
 * config plus abilities-by-id and primitives-by-slug; identity and
 * principals are not included (re-minted / re-specified on import).
 */
export async function exportVAU(instanceId: string, vauId: string) {
  const res = await http.get<VAUExport>(`${base(instanceId)}/${encodeURIComponent(vauId)}/export`);
  return res.data;
}

/**
 * Import a VAU from an export envelope into `instanceId`. The importer
 * supplies the new principal; name/slug optionally override the export's.
 * Abilities and primitives resolve against the target instance — anything
 * that doesn't resolve is dropped and surfaced in the response's
 * `unresolved_*` arrays (resolve-or-drop-and-report).
 */
export async function importVAU(instanceId: string, payload: VAUImportPayload) {
  // Provisions a fresh OBO token; see VAU_PROVISIONING_TIMEOUT_MS rationale.
  const res = await http.post<VAUImportResponse>(`${base(instanceId)}/import`, payload, {
    timeout: VAU_PROVISIONING_TIMEOUT_MS,
  });
  return res.data;
}

const directoryBase = `${HYPATIA_URL}/api/v1/directory/vau`;

/**
 * Directory templates with at least one live hire in the instance — the option
 * set for the list page's "Hired from directory" filter. Instance is resolved
 * from the request header server-side, so no instanceId in the path (matches the
 * other directory/vau routes). Empty list => no hires yet => filter hidden.
 */
export function listHiredDirectoryTemplates() {
  return async () => {
    const res = await http.get<HiredDirectoryTemplatesResponse>(`${directoryBase}/hired-templates`);
    return res.data;
  };
}
listHiredDirectoryTemplates.key = (instanceId: string) =>
  ['vau', 'directory', 'hired-templates', instanceId] as const;

export async function publishVAUToDirectory(slug: string, payload: PublishVAUPayload) {
  const res = await http.put<PublishVAUResponse>(
    `${directoryBase}/templates/${encodeURIComponent(slug)}`,
    payload,
    { timeout: VAU_PROVISIONING_TIMEOUT_MS }
  );
  return res.data;
}

/**
 * Download a VAU as a modular zip package (vau.json + per-primitive +
 * per-agent files). Triggers a browser download of `{slug}.vau.zip`.
 * `connectors` is the exporter-supplied list of required connector names
 * (explicit); the server stamps it into vau.json.
 */
export async function exportVAUZip(
  instanceId: string,
  vauId: string,
  downloadFilename: string,
  connectors: string[] = []
) {
  const params = new URLSearchParams();
  connectors.forEach(c => params.append('connectors', c));
  const query = params.toString();
  const res = await http.get(
    `${base(instanceId)}/${encodeURIComponent(vauId)}/export/zip${query ? `?${query}` : ''}`,
    {
      responseType: 'blob',
      timeout: 120_000,
    }
  );
  const url = URL.createObjectURL(res.data as Blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = downloadFilename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Import a VAU from a modular zip package. The zip is reassembled server-side
 * into the same envelope the JSON import consumes; form fields supply the new
 * principal and optional name/slug/conflict-mode overrides.
 */
export async function importVAUZip(
  instanceId: string,
  zipFile: File,
  fields: {
    principal_user_id: string;
    name?: string | null;
    slug?: string | null;
    conflict_mode?: 'overwrite' | 'skip';
  }
) {
  const formData = new FormData();
  formData.append('file', zipFile);
  formData.append('principal_user_id', fields.principal_user_id);
  if (fields.name) formData.append('name', fields.name);
  if (fields.slug) formData.append('slug', fields.slug);
  formData.append('conflict_mode', fields.conflict_mode ?? 'skip');
  const res = await http.post<VAUImportResponse>(`${base(instanceId)}/import/zip`, formData, {
    timeout: VAU_PROVISIONING_TIMEOUT_MS,
  });
  return res.data;
}
